import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useProject } from '@/context/ProjectContext';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  HelpCircle, 
  BookOpen, 
  MessageSquare, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle,
  User,
  Users,
  BarChart3,
  Shield,
  Lightbulb,
  Send,
  Loader2,
  Settings,
  Play
} from 'lucide-react';

// Embedded onboarding slides data
const ONBOARDING_SLIDES = [
  {
    id: 'welcome',
    title: 'Welcome to Pulse',
    subtitle: 'Understand how people really feel',
    description: 'Pulse helps gather authentic feedback from stakeholders, analyze perceptions, and drive meaningful and impactful change through data-driven insights.',
    image: 'https://static.prod-images.emergentagent.com/jobs/794f3022-4e48-459b-970c-fac2a3ecce33/images/c14c7927264a23b274e1c058e9679f3dbc6acbe1d28ac0fb131b059d7c9f5f2c.png',
    color: '#5AA2AE',
  },
  {
    id: 'about-me',
    title: 'About Me',
    subtitle: 'Share your perspective',
    description: 'Answer thoughtful questions about your experience and perceptions. Your responses help build a complete picture of your team\'s health and overall culture.',
    image: 'https://static.prod-images.emergentagent.com/jobs/7976a1f3-d14a-4ebd-8c5e-4b620b34d1b7/images/c3faeea0fdc26bd10f3e8e69d75cae7c5cf1d88a7dfcf6e4b62550a7c9403df7.png',
    color: '#6366f1',
  },
  {
    id: 'about-others',
    title: 'About Others',
    subtitle: 'Provide peer feedback',
    description: 'Share constructive feedback about colleagues and teams. This 360-degree approach creates a comprehensive view of relationships and collaboration.',
    image: 'https://static.prod-images.emergentagent.com/jobs/794f3022-4e48-459b-970c-fac2a3ecce33/images/ffb2b1a673392bfb3088f5c15d695fb3508b29773723a2c864a8db3a95b0c970.png',
    color: '#8b5cf6',
  },
  {
    id: 'dashboards',
    title: 'Dashboards',
    subtitle: 'See the insights',
    description: 'Explore interactive dashboards that provide an entirely new way to visualize stakeholder perceptions, identify trends, and highlight areas for improvement.',
    image: 'https://static.prod-images.emergentagent.com/jobs/794f3022-4e48-459b-970c-fac2a3ecce33/images/5880701dc693d94731e27385d9a68c371ab072aa47bad0148af026c4cf74764e.png',
    color: '#0ea5e9',
  },
  {
    id: 'safe-to-speak',
    title: 'Safe to Speak',
    subtitle: 'Your privacy is protected',
    description: 'Your responses are protected by anonymity thresholds and data aggregation. Individual answers are never shared, only collective insights to help the team improve.',
    image: 'https://static.prod-images.emergentagent.com/jobs/794f3022-4e48-459b-970c-fac2a3ecce33/images/5ef9a8264065716287d75bab4c6dcfe21a5c763974cf08843a605781b30d07eb.png',
    color: '#10b981',
    highlights: [
      'Responses are anonymized and aggregated',
      'Individual data is never shared',
      'Minimum response thresholds protect identity',
      'You control what you share',
    ],
  },
];

// FAQ data
const FAQ_ITEMS = [
  {
    question: 'What is Pulse and how does it work?',
    answer: 'Pulse is a stakeholder perception analysis platform that helps organizations gather authentic feedback, analyze perceptions, and drive meaningful change. Users answer questions about themselves (About Me) and provide feedback on colleagues (About Others). All responses are anonymized and aggregated to protect individual privacy while providing valuable insights.'
  },
  {
    question: 'Is my feedback really anonymous?',
    answer: 'Yes, your privacy is our top priority. Pulse uses anonymity thresholds and data aggregation to ensure individual responses cannot be identified. Your answers are combined with others before being shown in reports, and minimum response thresholds ensure that small groups cannot be singled out.'
  },
  {
    question: 'How often should I update my responses?',
    answer: 'You can update your responses at any time. We encourage regular updates as your perceptions and experiences change. This helps maintain an accurate picture of stakeholder sentiment over time and allows the organization to track improvements.'
  },
  {
    question: 'Who can see the dashboard results?',
    answer: 'Dashboard access depends on your role. Project administrators can see aggregated results and trends. Individual stakeholders can see their own responses and how they compare to anonymous group averages. No one can see individual responses from other users.'
  },
  {
    question: 'What are Drivers and Segments?',
    answer: 'Drivers are key themes or categories that questions are grouped into (e.g., Communication, Leadership, Culture). Segments are groups of stakeholders organized by characteristics like department, role, or location. These help organize and analyze feedback in meaningful ways.'
  },
  {
    question: 'Can I skip questions I don\'t want to answer?',
    answer: 'Yes, many questions can be skipped if you prefer not to answer. When skipping, you\'ll be asked to select a reason (e.g., "Not applicable", "Prefer not to answer"). This helps administrators understand the context of unanswered questions.'
  },
  {
    question: 'What is the People Galaxy view?',
    answer: 'The People Galaxy is an interactive visualization that shows the network of relationships between stakeholders. It helps identify collaboration patterns, communication flows, and potential gaps in how teams interact with each other.'
  },
  {
    question: 'How do I add someone who isn\'t in the stakeholder list?',
    answer: 'In the "About Others" section, you can add new stakeholders by clicking "Add New Stakeholder". Enter their details and select how they relate to you. The person will be added for admin review before becoming fully active in the system.'
  },
  {
    question: 'What happens to my data if I leave the organization?',
    answer: 'Your historical responses remain part of the aggregated data to maintain reporting continuity. However, your individual account can be deactivated, and you will no longer have access to the platform. Contact your administrator for specific data handling policies.'
  },
  {
    question: 'How can I get help or report an issue?',
    answer: 'Use the suggestion form on this Help page to submit feedback, report issues, or request new features. You can also email support directly at support@projectai.com. Our team typically responds within 24-48 hours.'
  }
];

// How to Use Pulse content
const HOW_TO_USE_SECTIONS = [
  {
    icon: User,
    title: 'Complete Your "About Me" Survey',
    color: '#6366f1',
    steps: [
      'Navigate to "About Me" from the sidebar',
      'Answer questions honestly about your experience and perceptions',
      'Use the slider, rating, and text fields to provide detailed feedback',
      'Skip questions that don\'t apply using the "Skip" button',
      'Your responses auto-save as you go'
    ]
  },
  {
    icon: Users,
    title: 'Provide Feedback on Others',
    color: '#8b5cf6',
    steps: [
      'Go to "About Others" in the sidebar',
      'Search for and select stakeholders you work with',
      'Define your relationship with each person',
      'Answer questions about your experience with them',
      'Add new stakeholders if someone isn\'t listed'
    ]
  },
  {
    icon: BarChart3,
    title: 'Explore the Dashboards',
    color: '#0ea5e9',
    steps: [
      'Visit "Project Summary" for an overview of results',
      'Use "Driver Analysis" to see performance by theme',
      'Check "Segment Analysis" to compare groups',
      'Explore "People Galaxy" to visualize relationships',
      'Export reports for presentations and meetings'
    ]
  },
  {
    icon: Shield,
    title: 'Understand Your Privacy',
    color: '#10b981',
    steps: [
      'Your individual responses are never shown to others',
      'Results require minimum response thresholds',
      'Data is always shown in aggregate form',
      'You can skip sensitive questions',
      'Review privacy reminders throughout the app'
    ]
  }
];

// Embedded Carousel Component
function EmbeddedCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slide = ONBOARDING_SLIDES[currentSlide];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 overflow-hidden" data-testid="help-carousel">
      <div className="p-6 md:p-8">
        <div className="grid md:grid-cols-2 gap-6 items-center">
          {/* Image */}
          <div className="flex justify-center">
            <div 
              className="relative w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden"
              style={{ backgroundColor: `${slide.color}10` }}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-contain p-3"
              />
            </div>
          </div>

          {/* Content */}
          <div className="text-center md:text-left">
            <Badge 
              className="mb-3"
              style={{ backgroundColor: `${slide.color}15`, color: slide.color }}
            >
              {slide.subtitle}
            </Badge>
            <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
              {slide.title}
            </h3>
            <p className="text-slate-600 mb-4 leading-relaxed text-sm md:text-base">
              {slide.description}
            </p>
            
            {slide.highlights && (
              <ul className="space-y-2">
                {slide.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-left text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{highlight}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-t border-slate-200">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
          disabled={currentSlide === 0}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        
        <div className="flex items-center gap-2">
          {ONBOARDING_SLIDES.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide 
                  ? 'w-6 h-2' 
                  : 'w-2 h-2 hover:bg-slate-400'
              }`}
              style={{ 
                backgroundColor: index === currentSlide ? slide.color : '#cbd5e1'
              }}
            />
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentSlide(Math.min(ONBOARDING_SLIDES.length - 1, currentSlide + 1))}
          disabled={currentSlide === ONBOARDING_SLIDES.length - 1}
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// Suggestion Form Component
function SuggestionForm() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    suggestion_type: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.suggestion_type || !formData.message.trim()) {
      toast.error('Please select a category and enter your suggestion');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/suggestions', {
        name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User',
        email: user?.email || '',
        suggestion_type: formData.suggestion_type,
        message: formData.message
      });
      setSubmitted(true);
      toast.success('Thank you! Your suggestion has been submitted.');
      setFormData({ suggestion_type: '', message: '' });
    } catch (err) {
      toast.error('Failed to submit suggestion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="suggestion-form">
      <div className="space-y-2">
        <Label htmlFor="type">Suggestion Category</Label>
        <Select
          value={formData.suggestion_type}
          onValueChange={(value) => setFormData(prev => ({ ...prev, suggestion_type: value }))}
        >
          <SelectTrigger data-testid="suggestion-type">
            <SelectValue placeholder="Select a category..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Feature Request">Feature Request</SelectItem>
            <SelectItem value="Bug Report">Bug Report</SelectItem>
            <SelectItem value="Usability Improvement">Usability Improvement</SelectItem>
            <SelectItem value="Documentation">Documentation</SelectItem>
            <SelectItem value="General Feedback">General Feedback</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Your Suggestion</Label>
        <Textarea
          id="message"
          value={formData.message}
          onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
          placeholder="Describe your suggestion, feedback, or issue in detail..."
          rows={5}
          required
          data-testid="suggestion-message"
        />
      </div>

      <div className="flex justify-end">
        <Button 
          type="submit" 
          disabled={submitting}
          className="bg-[#5AA2AE] hover:bg-[#4a8a94]"
          data-testid="suggestion-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Suggestion
            </>
          )}
        </Button>
      </div>

      {submitted && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5" />
          <span>Your suggestion has been sent to our team. Thank you for helping us improve!</span>
        </div>
      )}
    </form>
  );
}

export default function HelpPage() {
  const { isProjectAdmin } = useProject();
  const { projectId } = useParams();
  const navigate = useNavigate();

  return (
    <div className="space-y-8 max-w-5xl mx-auto animate-fade-in" data-testid="help-page">
      {/* Admin Setup Guide Banner */}
      {isProjectAdmin && (
        <div className="flex items-center justify-between p-4 bg-[#5AA2AE]/5 border border-[#5AA2AE]/20 rounded-lg"
             data-testid="admin-help-banner">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-[#5AA2AE]" />
            <div>
              <p className="text-sm font-medium text-slate-700">Admin Setup Guide</p>
              <p className="text-xs text-slate-500">Step-by-step instructions for configuring your Pulse project</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/project/${projectId}/admin-help`)}
            data-testid="go-to-admin-help-btn"
            className="border-[#5AA2AE] text-[#5AA2AE] hover:bg-[#5AA2AE] hover:text-white"
          >
            <Settings className="w-4 h-4 mr-1" /> View Setup Guide
          </Button>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Help & Support
        </h1>
        <p className="text-slate-600 mt-1">
          Learn how to use Pulse effectively and get answers to common questions
        </p>
      </div>

      {/* Quick Start Carousel */}
      <Card data-testid="quick-start-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#5AA2AE]" />
                Quick Start Guide
              </CardTitle>
              <CardDescription className="mt-1.5">
                A refresher on the key concepts and features of Pulse
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="retake-tour-btn"
              className="border-[#5AA2AE] text-[#5AA2AE] hover:bg-[#5AA2AE] hover:text-white"
              onClick={() => {
                const event = isProjectAdmin ? 'start-walkthrough' : 'start-user-walkthrough';
                window.dispatchEvent(new CustomEvent(event));
              }}
            >
              <Play className="w-4 h-4 mr-1" />
              Re-take Tour
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <EmbeddedCarousel />
        </CardContent>
      </Card>

      {/* How to Use Pulse */}
      <Card data-testid="how-to-use-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-[#5AA2AE]" />
            How to Use Pulse
          </CardTitle>
          <CardDescription>
            Step-by-step guides for getting the most out of the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {HOW_TO_USE_SECTIONS.map((section, idx) => (
              <div 
                key={idx} 
                className="p-5 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors"
                data-testid={`how-to-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${section.color}15` }}
                  >
                    <section.icon className="w-5 h-5" style={{ color: section.color }} />
                  </div>
                  <h3 className="font-semibold text-slate-900">{section.title}</h3>
                </div>
                <ol className="space-y-2">
                  {section.steps.map((step, stepIdx) => (
                    <li key={stepIdx} className="flex items-start gap-2 text-sm text-slate-600">
                      <span 
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${section.color}15`, color: section.color }}
                      >
                        {stepIdx + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card data-testid="faq-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#5AA2AE]" />
            Frequently Asked Questions
          </CardTitle>
          <CardDescription>
            Answers to common questions about using Pulse
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {FAQ_ITEMS.map((item, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} data-testid={`faq-item-${idx}`}>
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-medium text-slate-900">{item.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Suggestion Form */}
      <Card data-testid="suggestion-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#5AA2AE]" />
            Share Your Ideas
          </CardTitle>
          <CardDescription>
            Help us improve Pulse by sharing your feedback, suggestions, or reporting issues
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SuggestionForm />
        </CardContent>
      </Card>

      {/* Contact Info */}
      <div className="text-center py-6 border-t border-slate-200">
        <p className="text-slate-500 text-sm">
          Need more help? Contact us directly at{' '}
          <a 
            href="mailto:support@projectai.com" 
            className="text-[#5AA2AE] hover:underline font-medium"
          >
            support@projectai.com
          </a>
        </p>
      </div>
    </div>
  );
}
